"use client";

import {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
} from "react";

type Theme = "light" | "dark";

interface ThemeContextProps {
    theme: Theme;
    toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

interface ThemeProviderProps {
    children: ReactNode;
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
    const [theme, setTheme] = useState<Theme>("light");

    useEffect(() => {
        if (typeof window === "undefined") return;
        const savedTheme = localStorage.getItem("al-akhbar-theme") as Theme;
        if (savedTheme) {
            setTheme(savedTheme);
            document.documentElement.classList.remove("light", "dark");
            document.documentElement.classList.add(savedTheme);
        } else {
            if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
                setTheme("dark");
                document.documentElement.classList.add("dark");
            } else {
                setTheme("light");
            }
        }
    }, []);

    const toggleTheme = (newTheme?: Theme) => {
        setTheme((prevTheme) => {
            if (newTheme !== "dark" && newTheme !== "light") {
                newTheme = prevTheme === "light" ? "dark" : "light";
            }
            localStorage.setItem("al-akhbar-theme", newTheme);
            document.documentElement.classList.remove("light", "dark");
            document.documentElement.classList.add(newTheme);
            return newTheme;
        });
    };

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = (): ThemeContextProps => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error("useTheme must be used within a ThemeProvider");
    }
    return context;
};
